import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
  Fade,
  Collapse,
  IconButton
} from '@mui/material';
import { dashboardTheme } from '../styles/dashboardTheme';
import { formatDistanceToNow } from 'date-fns';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import WidgetInfoTooltip from './WidgetInfoTooltip';
import MoodIcon from '@mui/icons-material/Mood';
import TargetIcon from '@mui/icons-material/TrackChanges';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

/**
 * ActivityFeed Component
 * Shows recent activity across all features
 * @param {Array} activities - Initial 5 activities to display
 * @param {Object} fullActivityData - Object with all activities and metadata
 */
const ActivityFeed = ({ activities = [], fullActivityData }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Use fullActivityData if provided, otherwise fall back to old behavior
  const hasMore = fullActivityData?.hasMore || false;
  const totalCount = fullActivityData?.totalCount || activities.length;
  const displayActivities = expanded && fullActivityData 
    ? fullActivityData.items 
    : activities;

  const getActivityIcon = (type) => {
    switch (type) {
      case 'checkin':
        return <MoodIcon color="primary" />;
      case 'journal':
        return <EditIcon color="secondary" />;
      case 'goal':
        return <TargetIcon color="success" />;
      default:
        return <CheckCircleIcon />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'checkin':
        return 'primary';
      case 'journal':
        return 'secondary';
      case 'goal':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        height: '100%',
        maxHeight: expanded ? '600px' : '400px', // Limit max height even when expanded
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.05)',
        boxShadow: dashboardTheme.shadows.medium,
        transition: dashboardTheme.animations.transition,
        '&:hover': {
          boxShadow: dashboardTheme.shadows.large
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Recent Activity
          </Typography>
          <WidgetInfoTooltip id="activityFeed" />
        </Box>
        {totalCount > 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {expanded ? `Showing ${displayActivities.length} of ${totalCount}` : `${totalCount} total`}
          </Typography>
        )}
      </Box>

      {displayActivities.length > 0 ? (
        <List sx={{ flex: 1, overflow: 'auto' }}>
          {displayActivities.map((activity, index) => (
            <Fade in={true} timeout={300 + (index * 50)} key={index}>
              <ListItem 
                sx={{ 
                  px: 0,
                  py: 1,
                  borderBottom: index < displayActivities.length - 1 ? 1 : 0,
                  borderColor: 'divider'
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {getActivityIcon(activity.type)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {activity.title}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                    </Typography>
                  }
                />
              </ListItem>
            </Fade>
          ))}
        </List>
      ) : (
        <Box 
          sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'text.secondary'
          }}
        >
          <Typography variant="body2" align="center" gutterBottom>
            No recent activity
          </Typography>
          <Typography variant="caption" align="center">
            Start by checking in or writing a journal entry
          </Typography>
        </Box>
      )}

      {/* Only show button if there are more activities to display */}
      {hasMore && (
        <Button 
          variant="text" 
          size="small" 
          fullWidth
          onClick={handleToggleExpand}
          startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ 
            mt: 2,
            color: dashboardTheme.colors.primary.main,
            '&:hover': {
              background: 'rgba(251, 146, 60, 0.05)'
            }
          }}
        >
          {expanded ? 'Show Less' : `View All Activity (${totalCount})`}
        </Button>
      )}
    </Paper>
  );
};

export default ActivityFeed;